import type { QueryClient } from '@tanstack/react-query';
import {
  isTutorRuntimeQuotaQueryKey,
  TUTOR_QUERY_ROOT,
} from '../queryKeys';
import { runtimeQuotaApiFeatureName } from '../utils/runtimeQuotaFeatureIdentity';

export const TUTOR_PROGRESS_INVALIDATION_DEBOUNCE_MS = 400;

let debounceTimer: ReturnType<typeof setTimeout> | null = null;
let progressListenerCount = 0;
let progressCleanup: (() => void) | null = null;

function scheduleProgressBatchInvalidation(queryClient: QueryClient) {
  if (debounceTimer != null) {
    clearTimeout(debounceTimer);
  }
  debounceTimer = setTimeout(() => {
    debounceTimer = null;
    void queryClient.invalidateQueries({
      queryKey: [TUTOR_QUERY_ROOT, 'progress-batch'],
      refetchType: 'none',
    });
  }, TUTOR_PROGRESS_INVALIDATION_DEBOUNCE_MS);
}

function onProgressUpdated(queryClient: QueryClient) {
  scheduleProgressBatchInvalidation(queryClient);
}

/**
 * Single coalesced listener for `tkm-student-tutor-progress-updated`.
 * Reference-counted so Strict Mode remount does not leave duplicate listeners.
 */
export function acquireTutorProgressInvalidationBridge(queryClient: QueryClient): () => void {
  progressListenerCount += 1;
  if (progressListenerCount === 1) {
    const handler = () => onProgressUpdated(queryClient);
    window.addEventListener('tkm-student-tutor-progress-updated', handler);
    progressCleanup = () => {
      window.removeEventListener('tkm-student-tutor-progress-updated', handler);
      if (debounceTimer != null) {
        clearTimeout(debounceTimer);
        debounceTimer = null;
      }
    };
  }

  return () => {
    progressListenerCount = Math.max(0, progressListenerCount - 1);
    if (progressListenerCount === 0 && progressCleanup) {
      progressCleanup();
      progressCleanup = null;
    }
  };
}

/** Invalidate runtime quota queries (optional feature filter). */
export function invalidateTutorRuntimeQuota(
  queryClient: QueryClient,
  featureName?: string,
  options?: { refetchType?: 'active' | 'inactive' | 'all' | 'none' },
): Promise<void> {
  const apiFeature = featureName ? runtimeQuotaApiFeatureName(featureName) : undefined;
  return queryClient.invalidateQueries({
    queryKey: [TUTOR_QUERY_ROOT, 'runtime-quota'],
    predicate: apiFeature
      ? (query) => isTutorRuntimeQuotaQueryKey(query.queryKey, apiFeature)
      : undefined,
    refetchType: options?.refetchType ?? 'active',
  });
}

/** Test-only reset for bridge reference counting. */
export function __resetTutorProgressInvalidationBridgeForTests(): void {
  progressListenerCount = 0;
  if (progressCleanup) {
    progressCleanup();
    progressCleanup = null;
  }
  if (debounceTimer != null) {
    clearTimeout(debounceTimer);
    debounceTimer = null;
  }
}
